import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import ValidatedTextField from '../../../Components/ValidatedTextField';
import DateTimePicker from '../../../Components/DateTimePicker';
import { numberValidator, nameValidator } from '../../../utils/validators';
import TooltipWrapper from '../../../Components/TooltipWrapper';
import { toolTips } from '../../../utils/questionToolTips';
import { IRSQuestion } from '../../../types/irsForms';
import IRSFormOtherCategory from './IRSFormOtherCategory';
import {
  deleteIRSFormDocument,
  updateIRSFormAnswerWithFile,
  getIRSFormDocumentUrl,
} from '../../../endpoints/irsForms';
import { IRSDocumentResponse } from '../../../types/irsForms';

interface IRSFormQuestionProps {
  question: IRSQuestion;
  answer: string | number | boolean | null | undefined;
  onChange: (value: string | number | boolean | null) => void;
  onFormChange?: (isValid: boolean) => void;
  disabled?: boolean;
  errorOverride?: string | null;
  parentAnswer?: string | number | boolean | null;
  formId: string;
  onOtherItemsUpdate?: () => void;
  saveStatus?: 'idle' | 'saving' | 'saved' | 'error';
  saveError?: string;
  onRetrySave?: () => void;
  answerGroupId?: string; // For repeating answer groups
  isIndented?: boolean; // Whether this question should be indented
}

const IRSFormQuestion: React.FC<IRSFormQuestionProps> = ({
  question,
  answer,
  onChange,
  onFormChange,
  disabled = false,
  errorOverride = null,
  parentAnswer,
  formId,
  onOtherItemsUpdate,
  saveStatus = 'idle',
  saveError,
  onRetrySave,
  answerGroupId,
  isIndented = false,
}) => {
  // Track focus state to restore after save
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const wasFocusedRef = useRef<boolean>(false);
  const previousSaveStatusRef = useRef<'idle' | 'saving' | 'saved' | 'error'>(
    saveStatus
  );

  // Restore focus after save status changes
  useEffect(() => {
    // If status changed from 'saving' to 'saved' or 'idle', and input was focused, restore focus
    if (
      previousSaveStatusRef.current === 'saving' &&
      (saveStatus === 'saved' || saveStatus === 'idle') &&
      wasFocusedRef.current &&
      inputRef.current &&
      typeof window !== 'undefined' &&
      window.document &&
      window.document.activeElement !== inputRef.current &&
      !disabled
    ) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        if (inputRef.current && !disabled) {
          inputRef.current.focus();
          // For text inputs, move cursor to end
          if (
            inputRef.current instanceof HTMLInputElement ||
            inputRef.current instanceof HTMLTextAreaElement
          ) {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
          }
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    previousSaveStatusRef.current = saveStatus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveStatus, disabled]);

  const handleChange = (isValid: boolean, value: any) => {
    if (onFormChange) {
      onFormChange(isValid);
    }
    onChange(value);
  };

  const handleBooleanChange = (value: string) => {
    if (value === 'true') {
      onChange(true);
    } else if (value === 'false') {
      onChange(false);
    } else {
      onChange(null);
    }
  };

  // Document handling for image type
  const [document, setDocument] = useState<IRSDocumentResponse | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Initialize document from question.document (included in GET questions response)
  useEffect(() => {
    if (question.answer_type === 'image' && question.document) {
      // Use document metadata from question response
      setDocument({
        id: question.document.id,
        name: question.document.name,
        document_type: question.document.document_type,
      });
    } else if (
      question.answer_type === 'image' &&
      question.document_id &&
      !question.document
    ) {
      // Fallback: if document_id exists but document metadata not provided, create minimal document object
      setDocument({
        id: question.document_id,
        name: 'Document',
        document_type: 'other',
      });
    } else if (question.answer_type === 'image' && !question.document_id) {
      setDocument(null);
    }
  }, [question.answer_type, question.document_id, question.document]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Use FormData to upload via answer endpoint
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'supporting_document');

      await updateIRSFormAnswerWithFile(formId, question.question_id, formData);

      // After successful upload, trigger refetch to get updated document_id
      // The server will have set document_id and included document metadata in next fetch
      if (onOtherItemsUpdate) {
        onOtherItemsUpdate(); // This triggers refetch in parent
      }

      // Note: document_id will be updated when questions are refetched
      // For now, create a temporary document object with the file name
      setDocument({
        id: question.document_id || 'pending',
        name: file.name,
        document_type: 'supporting_document',
      });
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload document. Please try again.';
      alert(errorMessage);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async () => {
    if (!document || !question.document_id) return;

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeleting(true);
    try {
      // Use DELETE endpoint (if server supports it)
      await deleteIRSFormDocument(formId, document.id);

      // After successful deletion, trigger refetch to get updated state
      if (onOtherItemsUpdate) {
        onOtherItemsUpdate(); // This triggers refetch in parent
      }

      setDocument(null);
      // Clear the document_id - this will trigger auto-save to set document_id to null
      onChange(null);
    } catch (error: any) {
      console.error('Failed to delete document:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete document. Please try again.';
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadDocument = async () => {
    if (!document) return;

    try {
      // Get signed URL for download (expires after 1 hour)
      const response = await getIRSFormDocumentUrl(formId, document.id);
      window.open(response.url, '_blank');
    } catch (error: any) {
      console.error('Failed to download document:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to download document. Please try again.';
      alert(errorMessage);
    }
  };

  // Check if this conditional question should be shown
  const shouldShow = () => {
    if (!question.is_conditional || !question.parent_question_id) {
      return true;
    }

    if (parentAnswer === null || parentAnswer === undefined) {
      return false;
    }

    // For boolean parents, check if answer matches conditional_value
    if (typeof parentAnswer === 'boolean') {
      const conditionalValue = question.conditional_value;
      if (conditionalValue === '1' || conditionalValue === 'true') {
        return parentAnswer === true;
      }
      if (conditionalValue === '0' || conditionalValue === 'false') {
        return parentAnswer === false;
      }
      return String(parentAnswer) === conditionalValue;
    }

    // For numeric types (integer or decimal), evaluate comparison operators
    if (typeof parentAnswer === 'number') {
      return evaluateNumericCondition(
        parentAnswer,
        question.conditional_value || ''
      );
    }

    // For other types (text, date), compare as strings
    return String(parentAnswer) === question.conditional_value;
  };

  // Helper function to evaluate numeric conditions with comparison operators
  // Supports: >, <, >=, <=, !=, = (or no operator for equality)
  const evaluateNumericCondition = (
    actualValue: number,
    conditionalValue: string
  ): boolean => {
    if (!conditionalValue) {
      return false;
    }

    // Parse the conditional value to extract operator and threshold
    let operator = '=';
    let thresholdStr = conditionalValue;

    // Check for comparison operators at the start
    if (conditionalValue.startsWith('>=')) {
      operator = '>=';
      thresholdStr = conditionalValue.slice(2);
    } else if (conditionalValue.startsWith('<=')) {
      operator = '<=';
      thresholdStr = conditionalValue.slice(2);
    } else if (conditionalValue.startsWith('!=')) {
      operator = '!=';
      thresholdStr = conditionalValue.slice(2);
    } else if (conditionalValue.startsWith('>')) {
      operator = '>';
      thresholdStr = conditionalValue.slice(1);
    } else if (conditionalValue.startsWith('<')) {
      operator = '<';
      thresholdStr = conditionalValue.slice(1);
    } else if (conditionalValue.startsWith('=')) {
      operator = '=';
      thresholdStr = conditionalValue.slice(1);
    }

    // Parse threshold value (parseFloat works for both integers and decimals)
    const threshold = parseFloat(thresholdStr);

    if (isNaN(threshold)) {
      return false;
    }

    // Evaluate condition
    // Use small tolerance for equality/inequality comparisons to handle floating point precision
    switch (operator) {
      case '>':
        return actualValue > threshold;
      case '<':
        return actualValue < threshold;
      case '>=':
        return actualValue >= threshold;
      case '<=':
        return actualValue <= threshold;
      case '!=':
        return Math.abs(actualValue - threshold) >= 0.01;
      case '=':
      default:
        return Math.abs(actualValue - threshold) < 0.01;
    }
  };

  if (!shouldShow()) {
    return null;
  }

  const renderAnswerInput = () => {
    switch (question.answer_type) {
      case 'decimal':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <ValidatedTextField
              label=""
              validator={numberValidator}
              startAdornment="$"
              errorOverride={errorOverride}
              disabled={disabled}
              number={true}
              financial={true}
              value={answer as number | null | undefined}
              onChange={(isValid, value) => handleChange(isValid, value)}
              ref={inputRef}
              onFocus={() => {
                wasFocusedRef.current = true;
              }}
              onBlur={() => {
                wasFocusedRef.current = false;
              }}
            />
          </Box>
        );

      case 'text':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <ValidatedTextField
              label=""
              validator={nameValidator}
              errorOverride={errorOverride}
              disabled={disabled}
              value={answer as string | null | undefined}
              onChange={(isValid, value) => handleChange(isValid, value)}
              ref={inputRef}
              onFocus={() => {
                wasFocusedRef.current = true;
              }}
              onBlur={() => {
                wasFocusedRef.current = false;
              }}
            />
          </Box>
        );

      case 'date':
        // DateTimePicker doesn't support disabled prop directly, so we'll handle it differently
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <Box
              sx={{
                flex: 1,
                pointerEvents: disabled ? 'none' : 'auto',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              <DateTimePicker
                date={answer ? dayjs(answer as string) : null}
                label=""
                minDate={null}
                maxDate={null}
                handleDateChange={(value) =>
                  onChange(value ? value.format('YYYY-MM-DD') : null)
                }
                extraError={false}
                extraErrorMessage=""
                handleErrorUpdate={() => {}}
                shown={true}
                disableFuture={false}
                required={question.is_required}
              />
            </Box>
          </Box>
        );

      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <FormControl
              size="small"
              fullWidth
              error={!!errorOverride}
              disabled={disabled}
            >
              <Select
                value={
                  answer === null || answer === undefined
                    ? ''
                    : answer === true
                      ? 'true'
                      : 'false'
                }
                onChange={(event) => handleBooleanChange(event.target.value)}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
              {!!errorOverride && (
                <FormHelperText>{errorOverride}</FormHelperText>
              )}
            </FormControl>
          </Box>
        );

      case 'integer':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <ValidatedTextField
              label=""
              validator={numberValidator}
              errorOverride={errorOverride}
              disabled={disabled}
              number={true}
              value={answer as number | null | undefined}
              onChange={(isValid, value) => {
                // Ensure integer value (no decimals)
                if (value !== null && value !== '') {
                  // Handle the value - ValidatedTextField may pass a number or string
                  // If user types/pastes "1.00", we want to get 1, not 100
                  // Parse as float first to handle "1.00" correctly, then floor to integer
                  let numValue: number;
                  if (typeof value === 'string') {
                    // Parse as float first to handle "1.00" correctly
                    numValue = parseFloat(value);
                  } else {
                    // Already a number, use it directly
                    numValue = value;
                  }
                  // Floor to ensure integer (1.00 -> 1, 1.99 -> 1)
                  const intValue = isNaN(numValue) ? 0 : Math.floor(numValue);
                  handleChange(isValid, intValue);
                } else {
                  handleChange(isValid, null);
                }
              }}
              ref={inputRef}
              onFocus={() => {
                wasFocusedRef.current = true;
              }}
              onBlur={() => {
                wasFocusedRef.current = false;
              }}
            />
          </Box>
        );

      case 'image':
        return (
          <Box>
            {document && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={document.name || 'Document'}
                  onDelete={
                    disabled || deleting ? undefined : handleDeleteDocument
                  }
                  deleteIcon={
                    deleting ? <CircularProgress size={16} /> : <DeleteIcon />
                  }
                  onClick={handleDownloadDocument}
                  clickable
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            )}
            {!disabled && (
              <Button
                component="label"
                variant="contained"
                size="small"
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                disabled={uploading || deleting}
                fullWidth
              >
                {uploading
                  ? 'Uploading...'
                  : document
                    ? 'Replace Document'
                    : 'Upload Document'}
                <input
                  type="file"
                  hidden
                  onChange={handleFileUpload}
                  accept="image/*,.pdf,.doc,.docx"
                />
              </Button>
            )}
          </Box>
        );

      case 'other_category':
        return (
          <IRSFormOtherCategory
            formId={formId}
            questionId={question.question_id}
            items={question.other_line_items || []}
            editable={!disabled}
            onUpdate={onOtherItemsUpdate || (() => {})}
          />
        );

      case 'sub_total':
        // Sub_total questions are read-only and display calculated value in a disabled text box
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
              {question.question_number || ''}.
            </Typography>
            <TextField
              value={
                question.sub_total_value !== null &&
                question.sub_total_value !== undefined
                  ? question.sub_total_value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : '0.00'
              }
              disabled
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
              }}
              inputProps={{
                style: { textAlign: 'right' },
              }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'action.disabledBackground',
                  color: 'text.primary',
                  fontWeight: 'bold',
                  '&.Mui-disabled': {
                    WebkitTextFillColor: 'text.primary',
                    opacity: 0.8,
                  },
                },
                '& .MuiInputBase-input': {
                  fontWeight: 'bold',
                  textAlign: 'right',
                },
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Render save status indicator
  const renderSaveStatus = () => {
    if (saveStatus === 'idle') return null;

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
        {saveStatus === 'saving' && (
          <Tooltip title="Saving...">
            <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
          </Tooltip>
        )}
        {saveStatus === 'saved' && (
          <Tooltip title="Saved">
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
          </Tooltip>
        )}
        {saveStatus === 'error' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={saveError || 'Save failed'}>
              <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
            </Tooltip>
            {onRetrySave && (
              <Tooltip title="Retry save">
                <IconButton
                  size="small"
                  onClick={onRetrySave}
                  sx={{ p: 0.5 }}
                  color="error"
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Determine Grid layout based on indentation
  const questionGridSize = isIndented ? 7 : 9;
  const answerGridSize = 3;
  const spacingGridSize = isIndented ? 1 : 0;

  return (
    <Grid container sx={{ mb: 2 }}>
      {isIndented && <Grid item xs={spacingGridSize} />}
      <Grid item xs={questionGridSize} sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1">
            <b>{question.question_number || ''}.</b> {question.question_text}{' '}
            {question.is_required && (
              <Typography component="span" color="error">
                *
              </Typography>
            )}{' '}
            {question.helper_text && (
              <Tooltip
                title={
                  <Typography sx={{ whiteSpace: 'pre-line' }}>
                    {question.helper_text}
                  </Typography>
                }
                arrow
              >
                <InfoIcon
                  sx={{
                    fontSize: 18,
                    color: 'primary.main',
                    ml: 0.5,
                    verticalAlign: 'middle',
                    cursor: 'help',
                  }}
                />
              </Tooltip>
            )}
            {question.question_id &&
              Object.keys(toolTips).includes(question.question_id) && (
                <TooltipWrapper
                  message={toolTips[question.question_id]}
                  id={question.question_id}
                />
              )}
          </Typography>
          {renderSaveStatus()}
        </Box>
        {saveStatus === 'error' && saveError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mt: 0.5, display: 'block' }}
          >
            {saveError}
          </Typography>
        )}
      </Grid>
      {question.answer_type !== 'other_category' &&
        question.answer_type !== 'image' && (
          <Grid item xs={answerGridSize} sx={{ p: 2 }}>
            {renderAnswerInput()}
          </Grid>
        )}
      {isIndented && <Grid item xs={spacingGridSize} />}
      {(question.answer_type === 'other_category' ||
        question.answer_type === 'image') && (
        <Grid item xs={12} sx={{ p: 2 }}>
          {renderAnswerInput()}
        </Grid>
      )}
    </Grid>
  );
};

export default IRSFormQuestion;
